import { DependencyContainer, InjectionToken } from "@launchtray/tsyringe-async";
import { IModule } from "./IModule";
import { ParamDescription } from "./ParamDescription";

export abstract class BaseModule<TResult> implements IModule<TResult> {
    protected internalContainer: DependencyContainer;

    public constructor(container: DependencyContainer) {
        this.internalContainer = container;
    }

    public async build(): Promise<void> {
        this.internalContainer.registerInstance("IContextResolver", this);
    }

    public abstract start(): Promise<TResult>;

    public async resolve<T>(token: InjectionToken<T>): Promise<T> {
        return await this.internalContainer.resolve(token);
    }

    protected async resolveWithParams<T>(container: DependencyContainer, token: InjectionToken<T>, ...args: Array<ParamDescription<any>>): Promise<T> {
        const tempContainer = container.createChildContainer();
        for (const arg of args) {
            tempContainer.registerInstance(arg.token, arg.instance);
        }
        const instance = await tempContainer.resolve(token);
        tempContainer.clearInstances();
        tempContainer.reset();
        return instance;
    }

    protected registerModule<TModule extends IModule<TResult>, TResult>(ModuleCls: new (container: DependencyContainer) => TModule): DependencyContainer {
        return this.internalContainer.register(ModuleCls, {
            useFactory: async (container: DependencyContainer) => {
                const module = new ModuleCls(container.createChildContainer());
                await module.build();
                return module;
            },
        });
    }

    public dispose(): void {
        this.internalContainer.clearInstances();
        this.internalContainer.reset();
    }
}
